import {
  PolfEventHoleCompetitionTypeEnum,
  PolfEventModeEnum,
} from '@polf/shared/codegen/types'
import { formatChips } from '@polf/shared/locale'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useOutletContext } from 'react-router-dom'

import {
  type HoleCompetitionEntryFragment,
  type MobileHoleCompetitionFragment,
  namedOperations,
  useCurrentPlayerFlightScoresQuery,
  useCurrentPlayerHoleCompetitionEntryCreateMutation,
  useCurrentPlayerHoleCompetitionEntryRemoveMutation,
  useCurrentPlayerHoleCompetitionsQuery,
} from '../__generated__/hooks'
import type { CurrentPlayerCtx } from '../PlayerShell'
import { usesChips } from '../util/event-mode'
import { initialsOf } from '../util/initials'
import { SIDEGAME_BRAND } from '../util/sidegame-brand'

/**
 * Measured hole-competition side games (CLOSEST / LONGEST).
 *
 * Score-keeper flow: one player in the flight records entries for the
 * whole flight (some flights register the closest-to-pin distance for every
 * member who played the hole). The form has a player picker — chip row of
 * flight members, defaulting to the logged-in player. Entries are scoped
 * by the picked player, not by the auth identity.
 *
 * Beat-the-PRO is NOT listed here: its entries are bets (win/lose against
 * the pro), not measured values, so a min-value "leader" is meaningless.
 * Players meet the pro at the hole (Score-page banner) and results live on
 * the shared /b/ Beat-the-PRO view.
 */

type FlightMember = { id: string; name: string }

/**
 * Per-type form metadata layered on top of the shared brand identity.
 * Brand (label + icon + colors) comes from SIDEGAME_BRAND so banner +
 * card never disagree; only the form-input bits (unit, placeholder)
 * live here.
 */
const TYPE_META: Record<
  PolfEventHoleCompetitionTypeEnum,
  { unit: string; placeholder: string }
> = {
  [PolfEventHoleCompetitionTypeEnum.Closest]: {
    unit: 'cm',
    placeholder: 'f.eks. 240',
  },
  [PolfEventHoleCompetitionTypeEnum.Longest]: {
    unit: 'm',
    placeholder: 'f.eks. 245',
  },
  // BTP never renders on this page (excluded from the query) — entry kept
  // only for the Record's exhaustiveness.
  [PolfEventHoleCompetitionTypeEnum.Beatthepro]: {
    unit: '',
    placeholder: '',
  },
}

const Avatar = ({ name }: { name: string }) => (
  <div className="h-6 w-6 rounded-full bg-green-100 text-green-800 flex items-center justify-center text-[10px] font-bold shrink-0">
    {initialsOf(name)}
  </div>
)

const EntryRow = ({
  entry,
  rank,
  onRemove,
  removing,
  locked,
}: {
  entry: HoleCompetitionEntryFragment
  rank: number
  onRemove: () => void
  removing: boolean
  locked: boolean
}) => (
  <div className="flex items-center gap-2 px-2 py-1.5 bg-cream-card rounded text-xs">
    <span className="font-mono num text-ink-500 w-5 text-right">{rank}.</span>
    <Avatar name={entry.polfEventPlayer.name} />
    <span className="flex-1 font-semibold truncate">
      {entry.polfEventPlayer.name}
    </span>
    <span className="font-mono num font-bold text-green-700">
      {entry.value}
    </span>
    {locked ? null : (
      <button
        type="button"
        onClick={onRemove}
        disabled={removing}
        className="text-flag text-[10px] font-semibold disabled:opacity-40"
        aria-label="Fjern forsøk"
      >
        Fjern
      </button>
    )}
  </div>
)

const PlayerPicker = ({
  flight,
  selectedId,
  onSelect,
}: {
  flight: FlightMember[]
  selectedId: string
  onSelect: (id: string) => void
}) => (
  <div className="flex flex-wrap gap-1.5">
    {flight.map((p) => {
      const isSel = p.id === selectedId
      return (
        <button
          key={p.id}
          type="button"
          onClick={() => onSelect(p.id)}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            isSel ? 'bg-green-700 text-paper' : 'bg-cream-card text-ink-700'
          }`}
        >
          <Avatar name={p.name} />
          {p.name.split(' ')[0]}
        </button>
      )
    })}
  </div>
)

const EntryForm = ({
  comp,
  flight,
  defaultPlayerId,
  onSubmit,
  saving,
  onCancel,
}: {
  comp: MobileHoleCompetitionFragment
  flight: FlightMember[]
  defaultPlayerId: string
  onSubmit: (playerId: string, value: number) => void
  saving: boolean
  onCancel: () => void
}) => {
  const [playerId, setPlayerId] = useState(defaultPlayerId)
  // Look up the picked player's prior entry (one per (player, comp) since
  // the service now upserts). Pre-fill the value so the captain sees
  // "you're editing 240 cm" rather than starting from a blank input.
  const existing = useMemo(
    () => (comp.entries ?? []).find((e) => e.polfEventPlayer.id === playerId),
    [comp.entries, playerId]
  )
  const [value, setValue] = useState<string>(
    existing ? String(existing.value) : ''
  )
  // Reset the input when the picked player changes — track the last
  // seeded entry id so we don't fight the user's typing for the same
  // selection.
  const lastSeededRef = useRef<string | null>(existing?.id ?? null)
  useEffect(() => {
    const id = existing?.id ?? null
    if (id !== lastSeededRef.current) {
      lastSeededRef.current = id
      setValue(existing ? String(existing.value) : '')
    }
  }, [existing])
  const meta = TYPE_META[comp.type]
  const brand = SIDEGAME_BRAND[comp.type]
  const parsed = parseFloat(value.replace(',', '.'))
  const valid = !Number.isNaN(parsed) && parsed > 0
  const editing = !!existing
  return (
    <div className="flex flex-col gap-3 mt-3 p-3 bg-cream-card rounded">
      <div>
        <div className="up text-ink-500 mb-1.5">For hvem?</div>
        <PlayerPicker
          flight={flight}
          selectedId={playerId}
          onSelect={setPlayerId}
        />
      </div>
      {brand.helpText ? (
        <div className="text-[11px] text-ink-700 italic -mt-1">
          {brand.helpText}
        </div>
      ) : null}
      {editing ? (
        <div className="text-[11px] text-ink-700">
          Endrer eksisterende forsøk · forrige verdi{' '}
          <span className="font-mono num font-semibold text-ink-900">
            {existing.value}
            {meta.unit ? ` ${meta.unit}` : ''}
          </span>
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <input
          // The form is opened as an explicit user action; autofocus saves
          // the captain a tap on the input.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          type="text"
          inputMode="decimal"
          placeholder={meta.placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="flex-1 min-w-0 px-3 py-2 bg-paper rounded text-base font-mono num focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        {meta.unit ? (
          <span className="text-sm text-ink-500 font-medium">{meta.unit}</span>
        ) : null}
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 text-sm font-semibold text-ink-500"
        >
          Avbryt
        </button>
        <button
          type="button"
          disabled={!valid || saving}
          onClick={() => valid && onSubmit(playerId, parsed)}
          className="px-3 py-2 text-sm font-semibold rounded bg-green-700 text-paper disabled:opacity-40"
        >
          {editing ? 'Oppdater' : 'Lagre'}
        </button>
      </div>
    </div>
  )
}

const CompCard = ({
  comp,
  flight,
  defaultPlayerId,
  showChips,
  locked,
  highlight,
}: {
  comp: MobileHoleCompetitionFragment
  flight: FlightMember[]
  defaultPlayerId: string
  showChips: boolean
  locked: boolean
  /** Set when this card was the deep-link target — paints a brief ring. */
  highlight: boolean
}) => {
  const meta = TYPE_META[comp.type]
  const brand = SIDEGAME_BRAND[comp.type]
  const flightIds = useMemo(() => new Set(flight.map((p) => p.id)), [flight])
  const flightEntries = useMemo(
    () =>
      (comp.entries ?? []).filter((e) => flightIds.has(e.polfEventPlayer.id)),
    [comp.entries, flightIds]
  )
  // Both Closest and Longest are min-wins on this product: real-world
  // amateurs can't measure carry distance, so "Longest drive" is scored
  // as distance-to-flag (smallest wins). The backend's `currentLeader`
  // picks the smallest value regardless of type — the UI matches.
  const sorted = useMemo(() => {
    return (comp.entries ?? []).slice().sort((a, b) => a.value - b.value)
  }, [comp.entries])

  const [showForm, setShowForm] = useState(false)
  const [createEntry, { loading: creating }] =
    useCurrentPlayerHoleCompetitionEntryCreateMutation({
      refetchQueries: [namedOperations.Query.currentPlayerHoleCompetitions],
    })
  const [removeEntry, { loading: removing }] =
    useCurrentPlayerHoleCompetitionEntryRemoveMutation({
      refetchQueries: [namedOperations.Query.currentPlayerHoleCompetitions],
    })

  const { Icon } = brand

  const onSubmit = async (playerId: string, value: number) => {
    try {
      await createEntry({
        variables: {
          polfEventHoleCompetitionId: comp.id,
          createPolfEventHoleCompetitionEntryDto: {
            polfEventPlayerId: playerId,
            value,
          },
        },
      })
      setShowForm(false)
    } catch {
      // keep form open for retry
    }
  }

  const onRemove = async (id: string) => {
    try {
      await removeEntry({ variables: { polfEventHoleCompetitionEntryId: id } })
    } catch {
      // refetch reconciles
    }
  }

  return (
    <article
      id={`comp-${comp.id}`}
      // scroll-margin-top makes #comp-… anchor scroll clear the sticky
      // topbar (published height in --player-topbar-h).
      style={{ scrollMarginTop: 'calc(var(--player-topbar-h, 0px) + 12px)' }}
      className={`bg-paper rounded-lg shadow-sm p-3 flex flex-col gap-2 transition-shadow ${
        highlight ? 'ring-2 ring-green-500 shadow-md' : ''
      }`}
    >
      <header className="flex items-center gap-3">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center ${brand.tileBg}`}
        >
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-ink-900 truncate">
            Hull {comp.hole.number} · {brand.label}
          </div>
          {brand.helpText ? (
            <div className="text-[11px] text-ink-700 italic">
              {brand.helpText}
            </div>
          ) : null}
          {showChips && comp.chipValue > 0 ? (
            <div className="text-xs text-ink-500">
              Premie:{' '}
              <span className="font-mono num font-semibold text-gold">
                {formatChips(comp.chipValue)} chips
              </span>
            </div>
          ) : null}
        </div>
        {flightEntries.length > 0 ? (
          <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
            {flightEntries.length} fra flight
          </span>
        ) : null}
      </header>

      {comp.currentLeader ? (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-cream-card rounded text-xs">
          <span className="text-ink-500 font-medium">Ledelse</span>
          <Avatar name={comp.currentLeader.polfEventPlayer.name} />
          <span className="flex-1 truncate font-semibold">
            {comp.currentLeader.polfEventPlayer.name}
          </span>
          <span className="font-mono num font-bold text-green-700">
            {comp.currentLeader.value}
            {meta.unit ? ` ${meta.unit}` : ''}
          </span>
        </div>
      ) : (
        <p className="text-xs text-ink-500 italic">
          Ingen registrerte forsøk ennå
        </p>
      )}

      {flightEntries.length > 0 ? (
        <div className="flex flex-col gap-1">
          <div className="up text-ink-500">Fra din flight</div>
          {flightEntries
            .slice()
            .sort((a, b) => a.value - b.value)
            .map((e, i) => (
              <EntryRow
                key={e.id}
                entry={e}
                rank={i + 1}
                removing={removing}
                onRemove={() => onRemove(e.id)}
                locked={locked}
              />
            ))}
        </div>
      ) : null}

      {sorted.length > flightEntries.length ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-ink-500 font-semibold py-1">
            Se alle {sorted.length} forsøk
          </summary>
          <div className="flex flex-col gap-1 mt-2">
            {sorted.map((e, i) => (
              <div
                key={e.id}
                className="flex items-center gap-2 px-2 py-1.5 bg-cream-card rounded text-xs"
              >
                <span className="font-mono num text-ink-500 w-5 text-right">
                  {i + 1}.
                </span>
                <Avatar name={e.polfEventPlayer.name} />
                <span className="flex-1 font-semibold truncate">
                  {e.polfEventPlayer.name}
                </span>
                <span className="font-mono num font-bold text-green-700">
                  {e.value}
                </span>
              </div>
            ))}
          </div>
        </details>
      ) : null}

      {!locked &&
        (showForm ? (
          <EntryForm
            comp={comp}
            flight={flight}
            defaultPlayerId={defaultPlayerId}
            saving={creating}
            onSubmit={onSubmit}
            onCancel={() => setShowForm(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="self-start px-3 py-2 text-sm font-semibold rounded bg-cream-card text-ink-700 hover:bg-ink-100"
          >
            + Registrer forsøk
          </button>
        ))}
    </article>
  )
}

const SideGames = () => {
  const { player } = useOutletContext<{ player: CurrentPlayerCtx }>()
  const { data, loading, error } = useCurrentPlayerHoleCompetitionsQuery({
    variables: {
      holeCompetitionType: [
        PolfEventHoleCompetitionTypeEnum.Closest,
        PolfEventHoleCompetitionTypeEnum.Longest,
      ],
    },
    fetchPolicy: 'cache-and-network',
    // Poll so entries added on ANOTHER device (another flight member's phone,
    // the admin/operator) show up without a manual reload. Mutation refetches
    // only cover this device's own edits. 10s matches the leaderboard cadence.
    pollInterval: 10_000,
  })

  // Picker roster — mode-aware:
  //  - Scramble: one entry per TEAM (the team is the unit of play and
  //    registers a single attempt). Use `player.team.players` so the
  //    captain sees the teammates as the player options. Legacy
  //    behaviour, matches the side-game record-per-team model.
  //  - All other modes (POLF, Golf, BestBall): one entry per PLAYER.
  //    The score-keeper records entries for everyone in the flight,
  //    not just teammates — so the roster is the full flight, fetched
  //    via the flight-scores query.
  // The previous Scramble fix narrowed this to team-only for every
  // mode, which silently emptied the picker on individual events.
  const { data: flightData } = useCurrentPlayerFlightScoresQuery({
    fetchPolicy: 'cache-and-network',
    skip: player.polfEvent.mode === PolfEventModeEnum.Scramble,
  })
  const flight: FlightMember[] = useMemo(() => {
    if (player.polfEvent.mode === PolfEventModeEnum.Scramble) {
      return (player.team?.players ?? []).map((p) => ({
        id: p.id,
        name: p.name,
      }))
    }
    return (flightData?.currentPlayerFlightScores ?? []).map((p) => ({
      id: p.id,
      name: p.name,
    }))
  }, [
    player.polfEvent.mode,
    player.team?.players,
    flightData?.currentPlayerFlightScores,
  ])

  // Stable reference so the deep-link effect deps don't re-fire on every
  // render's fresh `?? []` empty array.
  const comps = useMemo(
    () => data?.currentPlayerHoleCompetitions ?? [],
    [data?.currentPlayerHoleCompetitions]
  )
  const showChips = usesChips(player.polfEvent.mode)
  const locked = player.scoresSubmitted || player.polfEvent.isReadOnly

  // Deep-link: /sidegames#comp-<id> scrolls to and briefly highlights the
  // target competition. Fired by the Score-page hole banner so the
  // captain lands directly on the relevant comp instead of having to
  // skim the list.
  const { hash } = useLocation()
  const [highlightId, setHighlightId] = useState<string | null>(null)
  const compsReady = comps.length > 0
  const lastHandledHash = useRef<string | null>(null)
  useEffect(() => {
    if (!hash || !hash.startsWith('#comp-') || !compsReady) return
    if (lastHandledHash.current === hash) return
    lastHandledHash.current = hash
    const id = hash.slice(1)
    const target = comps.find((c) => `comp-${c.id}` === id)
    if (!target) return
    // Defer one frame so the freshly-mounted article exists in the DOM.
    requestAnimationFrame(() => {
      document
        .getElementById(`comp-${target.id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setHighlightId(target.id)
      window.setTimeout(() => setHighlightId(null), 2000)
    })
  }, [hash, compsReady, comps])

  if (loading && !data) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster sidespill…
      </section>
    )
  }
  if (error) {
    return (
      <section className="px-4 py-8 text-center text-flag">
        Kunne ikke laste sidespillene.
      </section>
    )
  }
  if (!comps.length) {
    return (
      <section className="px-4 py-12 text-center">
        <h1 className="font-display text-xl font-bold text-green-900 mb-2">
          Sidespill
        </h1>
        <p className="text-ink-500 text-sm">
          Ingen hullkonkurranser på denne turneringen.
        </p>
      </section>
    )
  }

  const totalPrize = comps.reduce((s, c) => s + c.chipValue, 0)

  return (
    <section className="pb-4">
      <div className="px-4 pt-4 pb-2 flex items-end justify-between">
        <h1 className="font-display text-xl font-bold text-green-900">
          Sidespill
        </h1>
        <p className="text-xs text-ink-500 num">
          {comps.length} {comps.length === 1 ? 'konkurranse' : 'konkurranser'}
          {showChips && totalPrize > 0
            ? ` · pott ${formatChips(totalPrize)}`
            : ''}
        </p>
      </div>
      {locked ? (
        <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-green-100 border border-green-500 text-green-800 text-xs">
          {player.polfEvent.isReadOnly
            ? 'Turneringen er avsluttet — ingen nye forsøk kan registreres.'
            : 'Scorekortet er levert — ingen nye forsøk kan registreres.'}
        </div>
      ) : null}
      <div className="px-2 flex flex-col gap-3">
        {comps
          .slice()
          .sort((a, b) => a.hole.number - b.hole.number)
          .map((c) => (
            <CompCard
              key={c.id}
              comp={c}
              flight={flight}
              defaultPlayerId={player.id}
              showChips={showChips}
              locked={locked}
              highlight={highlightId === c.id}
            />
          ))}
      </div>
    </section>
  )
}

export default SideGames
